import { computed } from 'vue';
import { useUserStore } from '@/common/stores/userStore';
import { authService } from '@/features/common/auth/services/authService';
import { USER_TYPE } from '@/common/constants';
import { logger } from '@/common/services/config';

export function useUser() {
  const userStore = useUserStore();
  const isLoading = computed(() => userStore.isLoading);
  const isAuthenticated = computed(() => userStore.isAuthenticated);
  const userName = computed(() => userStore.userName);
  const userEmail = computed(() => userStore.userEmail);
  const userRoles = computed(() => userStore.userRoles);
  const accessToken = computed(() => userStore.accessToken);
  const lastAction = computed(() => userStore.lastAction);
  const lastActionPayload = computed(() => userStore.lastActionPayload);
  const account = computed(() => userStore.account);

  /**
   * Type d'utilisateur (Student | Collaborateur | Administrator),
   * dérivé des rôles Azure AD (UserRole). Mapping 1:1.
   */
  const userType = computed(() => userStore.userType);

  const isStudent = computed(() => userType.value === USER_TYPE.STUDENT);
  const isCollaborateur = computed(() => userType.value === USER_TYPE.COLLABORATEUR);
  const isAdministrator = computed(() => userType.value === USER_TYPE.ADMINISTRATOR);
  /** true si Collaborateur OU Administrator (tout sauf Student) */
  const isStaff = computed(() => isCollaborateur.value || isAdministrator.value);

  /** @deprecated Utiliser `userType` à la place */
  const userRole = userType;

  const login = async (email?: string) => {
    try {
      await authService.signIn(email);
    } catch (error) {
      logger.error('Login failed:', error);
      throw error;
    }
  };

  const loginWithDifferentAccount = async (email?: string) => {
    try {
      await authService.signInWithDifferentAccount(email);
    } catch (error) {
      logger.error('Login with different account failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.signOut();
    } catch (error) {
      logger.error('Logout failed:', error);
      throw error;
    }
  };

  const getToken = async () => {
    try {
      return await authService.getAccessToken();
    } catch (error) {
      logger.error('Get token failed:', error);
      return null;
    }
  };

  return {
    isAuthenticated,
    userName,
    userEmail,
    userRoles,
    userType,
    /** @deprecated Utiliser `userType` à la place */
    userRole,
    isStudent,
    isCollaborateur,
    isAdministrator,
    isStaff,
    accessToken,
    lastAction,
    lastActionPayload,
    account,
    isLoading,
    login,
    loginWithDifferentAccount,
    logout,
    getToken,
  };
}
